import { useCallback, useEffect, useState } from "react";
import { api, fileUrl } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Award as AwardIcon } from "lucide-react";
import UploadImage from "@/components/UploadImage";

export default function Awards() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/awards");
      setItems(data || []);
    } catch (err) {
      console.error("[Awards] load failed:", err?.message || err);
      toast.error("Failed to load awards");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return toast.error("Title is required");
    setSaving(true);
    try {
      await api.post("/awards", { title: title.trim(), image_url: imageUrl || null });
      toast.success("Award added");
      setTitle("");
      setImageUrl("");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to add");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/awards/${id}`);
      setItems((prev) => prev.filter((i) => i.id !== id));
      toast.success("Award removed");
    } catch (_e) {
      toast.error("Failed to remove");
    }
  };

  return (
    <div className="space-y-6 max-w-4xl" data-testid="awards-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Awards & recognition</h1>
        <p className="text-slate-500 mt-1 text-sm">Show off the badges, ribbons, and trophies you've earned.</p>
      </div>

      <form onSubmit={submit} className="bg-white border border-slate-200 rounded-xl p-5 space-y-4" data-testid="awards-form">
        <div>
          <Label htmlFor="a-title">Title</Label>
          <Input id="a-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Best Tacos 2025" data-testid="awards-title" required />
        </div>
        <div>
          <Label>Image (optional)</Label>
          <div className="mt-2">
            <UploadImage value={imageUrl} onChange={(v) => setImageUrl(v || "")} testid="awards-image" />
          </div>
        </div>
        <div className="flex justify-end">
          <Button type="submit" className="ff-btn-primary" disabled={saving} data-testid="awards-add">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus size={14} className="mr-1" />} Add award
          </Button>
        </div>
      </form>

      <div className="space-y-3">
        <h2 className="text-base font-semibold">Your awards · {items.length}</h2>
        {loading ? (
          <div className="text-slate-500 text-sm">Loading…</div>
        ) : items.length === 0 ? (
          <div className="text-slate-500 text-sm bg-white border border-dashed border-slate-200 rounded-xl p-8 text-center" data-testid="awards-empty">
            <AwardIcon className="mx-auto mb-2 text-slate-400" />
            No awards yet. Add your first recognition above.
          </div>
        ) : (
          <ul className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="awards-list">
            {items.map((a) => (
              <li key={a.id} className="bg-white border border-slate-200 rounded-xl p-3" data-testid={`award-${a.id}`}>
                {a.image_url ? (
                  <img src={fileUrl(a.image_url)} alt={a.title} className="w-full aspect-square object-cover rounded-md mb-2" />
                ) : (
                  <div className="w-full aspect-square bg-amber-50 rounded-md grid place-items-center mb-2">
                    <AwardIcon className="text-amber-500" size={36} />
                  </div>
                )}
                <div className="font-medium text-sm truncate">{a.title}</div>
                <button
                  type="button"
                  onClick={() => remove(a.id)}
                  className="mt-2 text-xs text-slate-500 hover:text-red-600 inline-flex items-center gap-1"
                  data-testid={`award-delete-${a.id}`}
                >
                  <Trash2 size={12} /> Remove
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
